import { Button, TextButton } from '@creditornot/cb-button';
import { Checkbox } from '@creditornot/cb-checkbox';
import { CaretDown } from '@creditornot/cb-icons';
import { Typography as T, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import React, { ChangeEvent, FC, Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { VENUE_STATUS_TITLE } from '../helpers';
import { VenueAvailabilityStatus } from '../types';

type Props = {
  selectedAvailabilityStatuses: VenueAvailabilityStatus[];
  handleSelect: (availabilityStatuses: VenueAvailabilityStatus[]) => void;
};

const AvailabilityStatusFilter: FC<Props> = ({ selectedAvailabilityStatuses, handleSelect }) => {
  const intl = useIntl();
  return (
    <Popover
      content={
        <div data-testid="availability-status-filter-popover">
          <AvailabilityStatusPopover
            selectedAvailabilityStatuses={selectedAvailabilityStatuses}
            handleSelect={handleSelect}
          />
        </div>
      }
      showOnClick
      showOnHover={false}
      renderArrow={false}
      placement="bottom"
    >
      <Button
        variant={selectedAvailabilityStatuses.length > 0 ? 'lightBlue' : 'white'}
        size="small"
        icon={<CaretDown />}
        iconPosition="right"
      >
        {selectedAvailabilityStatuses.length > 0 ? (
          <T.Body3>
            {selectedAvailabilityStatuses.map((status, index) => (
              <Fragment key={index}>
                <FormattedMessage id={VENUE_STATUS_TITLE[status]} />
                {index < selectedAvailabilityStatuses.length - 1 && ', '}
              </Fragment>
            ))}
          </T.Body3>
        ) : (
          <T.Body3>{intl.formatMessage({ id: 'venue-availability-status.table.venue-status' })}</T.Body3>
        )}
      </Button>
    </Popover>
  );
};

const PopoverWrapper = styled.div`
  width: 240px;
  padding-inline: 8px;
`;

const PopoverTitle = styled(T.Small)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const StatusesContainer = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  margin-block: 14px 8px;
`;
const StyledCheckbox = styled(Checkbox)`
  margin-block-end: 8px;
`;
const SelectAllContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-block-end: 4px;
`;

type PopoverProps = {
  selectedAvailabilityStatuses: VenueAvailabilityStatus[];
  handleSelect: (availabilityStatuses: VenueAvailabilityStatus[]) => void;
};

const allAvailabilityStatuses = Object.values(VenueAvailabilityStatus);

const AvailabilityStatusPopover: FC<PopoverProps> = ({ selectedAvailabilityStatuses, handleSelect }) => {
  const intl = useIntl();

  const handleSelectAll = () => handleSelect([...allAvailabilityStatuses]);
  const handleDeselectAll = () => handleSelect([]);
  const handleCheckboxChange =
    (availabilityStatus: VenueAvailabilityStatus) => (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        handleSelect([...selectedAvailabilityStatuses, availabilityStatus]);
      } else {
        handleSelect(
          selectedAvailabilityStatuses.filter(
            selectedAvailabilityStatuse => !selectedAvailabilityStatuse.includes(availabilityStatus),
          ),
        );
      }
    };
  return (
    <PopoverWrapper>
      <PopoverTitle>{intl.formatMessage({ id: 'venue-availability-status.table.venue-status' })}</PopoverTitle>
      <StatusesContainer>
        {allAvailabilityStatuses.map(status => (
          <StyledCheckbox
            key={status}
            checked={selectedAvailabilityStatuses.includes(status)}
            onChange={handleCheckboxChange(status)}
          >
            <T.Body3>
              <FormattedMessage id={VENUE_STATUS_TITLE[status]} />
            </T.Body3>
          </StyledCheckbox>
        ))}
      </StatusesContainer>
      <SelectAllContainer>
        {allAvailabilityStatuses.length > selectedAvailabilityStatuses.length && (
          <TextButton size="small" onClick={handleSelectAll}>
            <FormattedMessage id="venue-availability-status.filters.select-all" />
          </TextButton>
        )}
        {selectedAvailabilityStatuses.length > 0 && (
          <TextButton size="small" onClick={handleDeselectAll} variant="red">
            <FormattedMessage id="venue-availability-status.filters.deselect-all" />
          </TextButton>
        )}
      </SelectAllContainer>
    </PopoverWrapper>
  );
};

export default AvailabilityStatusFilter;
